import { useEffect, useState, type ComponentType } from 'react';
import { importWithRetry } from '../lib/importWithRetry';

/**
 * Client-only dynamic import as plain state: returns null until the chunk
 * loads (and always on the server), then the component. Lets callers render
 * their own fallback without a Suspense boundary.
 */
export function useLazyComponent<P>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
): ComponentType<P> | null {
  const [Component, setComponent] = useState<ComponentType<P> | null>(null);

  useEffect(() => {
    let cancelled = false;
    importWithRetry(importFn)().then(mod => {
      if (!cancelled) setComponent(() => mod.default);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; importFn is a new closure every render
  }, []);

  return Component;
}
